/**
 * Utilidades puras para sincronización de stock Flexy
 */
import { FLEXY_STOCK_CRON_PATTERN } from '../constants/flexyStock'
import type {
  FlexyStockStatusResponse,
  FlexySyncAllJob,
  FlexyStockSyncProgress,
  FlexyWarehouseDetail,
} from '../types/cron/flexyStock'
import type { CronTaskStatusResponse, CronTaskTimeUntil } from '../types/cron/cronTasks'

const BOGOTA_TZ = 'America/Bogota'

/**
 * Próxima ejecución cada 30 minutos (minutos 0 y 30) en America/Bogota
 */
function getNextFlexyStockExecution(): Date {
  const now = new Date()
  const bogotaParts = new Intl.DateTimeFormat('en-CA', {
    timeZone: BOGOTA_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  }).formatToParts(now)

  const get = (type: string) =>
    parseInt(bogotaParts.find((p) => p.type === type)?.value ?? '0', 10)

  const y = get('year')
  const m = get('month')
  const d = get('day')
  const hour = get('hour')
  const minute = get('minute')

  const nextMinute = minute < 30 ? 30 : 0
  const addHour = minute >= 30 ? 1 : 0
  const nextHour = (hour + addHour) % 24
  const addDay = minute >= 30 && hour === 23 ? 1 : 0

  const nextDate = new Date(y, m - 1, d + addDay)
  const yy = nextDate.getFullYear()
  const mm = nextDate.getMonth() + 1
  const dd = nextDate.getDate()

  const iso = `${yy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}T${String(nextHour).padStart(2, '0')}:${String(nextMinute).padStart(2, '0')}:00-05:00`
  return new Date(iso)
}

function buildTimeUntil(nextExecution: Date): CronTaskTimeUntil {
  const totalMs = Math.max(0, nextExecution.getTime() - Date.now())
  const hours = Math.floor(totalMs / (1000 * 60 * 60))
  const minutes = Math.floor((totalMs % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((totalMs % (1000 * 60)) / 1000)
  return { hours, minutes, seconds, totalMilliseconds: totalMs }
}

function buildHumanReadable(timeUntil: CronTaskTimeUntil): string {
  const parts: string[] = []
  if (timeUntil.hours > 0) parts.push(`${timeUntil.hours}h`)
  if (timeUntil.minutes > 0) parts.push(`${timeUntil.minutes}m`)
  if (timeUntil.seconds > 0 || parts.length === 0) parts.push(`${timeUntil.seconds}s`)
  return parts.join(' ')
}

/**
 * Adaptar GET /flexy/stock/status al formato estándar de tarjetas cron
 */
export function adaptFlexyStockStatusResponse(
  response: FlexyStockStatusResponse
): CronTaskStatusResponse {
  const enabled =
    response.enabled && response.sync_enabled && response.configured
  const nextExecution = getNextFlexyStockExecution()
  const nextExecutionISO = nextExecution.toISOString()
  const timeUntilNext = buildTimeUntil(nextExecution)

  const jobRunning = response.sync_all_job?.running === true

  return {
    success: response.success,
    message: response.message ?? 'OK',
    task: {
      enabled,
      frequency: 'Cada 30 minutos',
      cronPattern: FLEXY_STOCK_CRON_PATTERN,
      timezone: BOGOTA_TZ,
      hasTask: enabled,
      nextExecution: nextExecutionISO,
      nextExecutionISO,
      timeUntilNext,
      humanReadable: jobRunning
        ? 'Sincronización en curso...'
        : buildHumanReadable(timeUntilNext),
      executionSchedule: {
        hours: [],
        description: 'Cada 30 minutos (America/Bogota)',
      },
      currentTime: new Date().toISOString(),
      currentTimeBogota: new Date().toLocaleString('es-CO', {
        timeZone: BOGOTA_TZ,
      }),
    },
    timestamp: new Date().toISOString(),
  }
}

export function getFlexySnapshotCount(
  response: FlexyStockStatusResponse
): number | null {
  if (typeof response.snapshot_count === 'number') {
    return response.snapshot_count
  }
  return null
}

export function resolveWarehouseLabel(
  job: FlexySyncAllJob | undefined,
  warehouseId: number | null | undefined
): string | null {
  if (warehouseId == null || !job) return null
  const detail = job.warehouse_details?.find((w) => w.idalmacen === warehouseId)
  return detail?.nombre ?? `Bodega ${warehouseId}`
}

export function buildFlexySyncProgress(
  job: FlexySyncAllJob | undefined
): FlexyStockSyncProgress {
  const total = job?.total_warehouses ?? 0
  const completed = job?.completed_count ?? 0
  const currentId = job?.current_warehouse ?? null
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0

  return {
    completedCount: completed,
    totalWarehouses: total,
    currentWarehouseId: currentId,
    currentWarehouseName: resolveWarehouseLabel(job, currentId),
    percent,
  }
}

export function findWarehouseById(
  warehouses: FlexyWarehouseDetail[],
  idalmacen: number
): FlexyWarehouseDetail | undefined {
  return warehouses.find((w) => w.idalmacen === idalmacen)
}

export function formatFlexyLastSyncedAt(
  lastSyncedAt: string | null | undefined
): string {
  if (!lastSyncedAt) return 'Sin sincronización registrada'
  const date = new Date(lastSyncedAt)
  if (Number.isNaN(date.getTime())) return lastSyncedAt
  return date.toLocaleString('es-CO', { timeZone: BOGOTA_TZ })
}

export function readFlexyApiErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const data = (error as { response?: { data?: { message?: string; code?: string } } })
      .response?.data
    if (data?.message) return data.message
    if (data?.code) return data.code
  }
  if (error instanceof Error) return error.message
  return 'Error desconocido al sincronizar stock Flexy'
}
